import { name } from "./__init__";
import { createSelector } from "reselect";
import constants from "../../utils/constants";
import { Map, List, Set, fromJS } from "immutable";
import { dateDiffInDays, formatDate, isRangeContained } from "../../utils/date";
import { sizes } from "../../utils/media";
import {
  getFromSessionStorage,
  transformBookableWithCurrencyConversion,
  ariElmentSortBy,
  calculateServiceRate,
  getCityTaxConfiguration,
} from "../../utils/misc";
import moment from "moment";
import { selectors as reservationSelectors } from "../../containers/ReservationManager";
import { cartSelector } from "../ReservationManager/selectors";
import { parse as queryStringParse } from "query-string";
import get from "lodash/get";
import { white } from "../../theme/variables";

function getFirstApplicablePolicy(ticker, conditions, startDate, endDate) {
  return (
    conditions[ticker].cancellationPolicies &&
    conditions[ticker].cancellationPolicies
      // Checks if startDate, endDate are within valid range
      .filter(cancellationPolicy =>
        isRangeContained(
          startDate,
          endDate,
          cancellationPolicy.validFrom,
          cancellationPolicy.validTo
        )
      )[0]
  );
}

export function getCancellationPoliciesInfo(propertyARI, propertyTicker, startDate, endDate) {
  const conditions =
    (propertyARI.get(propertyTicker).get("conditions") &&
      propertyARI.get(propertyTicker).get("conditions").toJS()) ||
    {};
  return Object.keys(conditions).reduce((accum, it) => {
    const sortedCancellationPolicy = getFirstApplicablePolicy(it, conditions, startDate, endDate);
    // Clones the array of objects if it exists, so not to mutate the original

    return {
      ...accum,
      [it]: sortedCancellationPolicy && sortedCancellationPolicy.description,
    };
  }, {});
}

export function getCancellationRestrictions(propertyARI, propertyTicker, startDate, endDate) {
  function standardizeReleaseFormat(configurations, startDateLocal) {
    if (!configurations || configurations.length === 0) {
      return undefined;
    } else if (typeof configurations[0].maxRelease === "string") {
      return configurations;
    }
    return configurations.map(configuration => {
      // TODO: change this when backend doesn't send a nested object anymore
      const localConfiguration = JSON.parse(JSON.stringify(configuration));

      const maxReleaseDuration =
        localConfiguration.maxRelease && localConfiguration.maxRelease.duration;
      const maxReleaseChronoUnit =
        (localConfiguration.maxRelease && localConfiguration.maxRelease.chronoUnit) || "DAYS";
      const minReleaseDuration =
        localConfiguration.minRelease && localConfiguration.minRelease.duration;
      const minReleaseChronoUnit =
        (localConfiguration.minRelease && localConfiguration.minRelease.chronoUnit) || "DAYS";
      localConfiguration.maxRelease =
        maxReleaseDuration === undefined || maxReleaseDuration === null
          ? undefined
          : moment(startDateLocal)
              .subtract(maxReleaseDuration, [maxReleaseChronoUnit])
              .format("YYYY-MM-DD");
      localConfiguration.minRelease =
        minReleaseDuration === undefined || minReleaseDuration === null
          ? undefined
          : moment(startDateLocal)
              .subtract(minReleaseDuration, [minReleaseChronoUnit])
              .format("YYYY-MM-DD");

      return localConfiguration;
    });
  }

  const conditions =
    (propertyARI.get(propertyTicker).get("conditions") &&
      propertyARI.get(propertyTicker).get("conditions").toJS()) ||
    {};
  return Object.keys(conditions).reduce((accum, it) => {
    const sortedCancellationPolicy = getFirstApplicablePolicy(it, conditions, startDate, endDate);
    // Clones the array of objects if it exists, so not to mutate the original
    const sortedCancellationPolicyConfigurations = sortedCancellationPolicy && [
      ...sortedCancellationPolicy.configurations,
    ];
    const standarizedConfigurations = standardizeReleaseFormat(
      sortedCancellationPolicyConfigurations,
      startDate
    );
    return {
      ...accum,
      [it]: standarizedConfigurations,
    };
  }, {});
}
// getModelSelector :: State -> Model
export const getModelSelector = state => state[name];
export const getRoomSelectionModelSelector = state => state.roomSelection;
export const propsSelector = (_, props) => props; // eslint-disable-line

export const widthSelector = state => state.ui && state.ui.width;

export const heightSelector = state => state.ui && state.ui.height;
export const isReservationLoadedSelector = globalState =>
  globalState[name] &&
  globalState[name].get("reservation") &&
  !globalState[name].get("reservationLoading");

export const isDisplayActiveSelector = createSelector(widthSelector, width => {
  return width >= 1280;
});

export const restoredSessionSelector = createSelector(
  getModelSelector,
  model => model && model.get("restoredSession")
);

export const reservationCreatedSelector = createSelector(
  getModelSelector,
  model => model && model.get("reservationCreated")
);

export const globalLoadingSelector = createSelector(
  getModelSelector,
  model => model && model.get("globalLoading")
);

export const ariLoadingSelector = createSelector(
  getModelSelector,
  model => model && model.get("ariLoading")
);

export const analyticsLoadedSelector = createSelector(
  getModelSelector,
  model => model && model.get("analyticsLoaded")
);

export const tripAdvisorLoadedSelector = createSelector(
  getModelSelector,
  model => model && model.get("tripAdvisorLoaded")
);

export const externalMarkupLoadedSelector = createSelector(
  getModelSelector,
  model => model && model.get("externalMarkupLoaded")
);

export const trackingDataSelector = createSelector(
  getModelSelector,
  model => model && model.get("trackingData")
);

export const propertyTickerSelector = createSelector(getModelSelector, model => {
  return model && model.get("propertyTicker");
});

export const propertyTypeSelector = createSelector(
  getModelSelector,
  propertyTickerSelector,
  (model, propertyTicker) => {
    return (
      model &&
      model.get(constants.PROPERTY_ARI_KEY) &&
      model.get(constants.PROPERTY_ARI_KEY).get(propertyTicker) &&
      model.get(constants.PROPERTY_ARI_KEY).get(propertyTicker).get("type")
    );
  }
);

export const configurationSelector = createSelector(
  getModelSelector,
  model => model && model.get(constants.CONFIGURATION_KEY)
);

export const occupantRestrictionSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("occupantRestriction")
);

export const priceCalendarSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("priceCalendar")
);

export const submittedPromoCodesSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("promoCodes")
);

export const fullWidthSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("fullWidth")
);

export const fullWidthTypeSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("fullWidthType")
);

export const channelSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("channel")
);

export const showBookingOptionSummarySelector = createSelector(
  configurationSelector,
  fullWidthSelector,
  (configuration, fullWidth) => {
    if (fullWidth) {
      return false;
    }

    return configuration && configuration.get("showBookingOptionSummary");
  }
);

export const currencySelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("currency")
);

export const roomDenominationSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("roomDenomination")
);

export const pendingAmountTextConfigurationSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("pendingAmountTextConfiguration")
);

export const roomRepresentationTypeSelector = createSelector(
  configurationSelector,
  configuration =>
    (configuration && configuration.get("roomRepresentationType")) ||
    constants.ROOM_REPRESENTATION_TYPE.CAROUSEL
);

export const roomDetailRepresentationTypeSelector = createSelector(
  configurationSelector,
  fullWidthSelector,
  (configuration, fullWidth) => {
    if (fullWidth) {
      return constants.ROOM_DETAIL_REPRESENTATION_TYPE.FULL;
    }
    return (
      (configuration && configuration.get("roomDetailRepresentationType")) ||
      constants.ROOM_DETAIL_REPRESENTATION_TYPE.EMBEDDED
    );
  }
);

export const availableCurrenciesSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("availableCurrencies")
);

export const currencyExchangeSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("currencyExchange")
);

export const localeSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("locale")
);

export const availableLocalesSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("availableLocales")
);

export const defaultLocaleSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("defaultLocale")
);

export const endDateSelector = createSelector(
  configurationSelector,
  configuration => configuration && moment(configuration.get("endDate")).startOf("day").toDate()
);

export const startDateSelector = createSelector(
  configurationSelector,
  configuration => configuration && moment(configuration.get("startDate")).startOf("day").toDate()
);

export const iframeModeSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("iframeMode")
);

export const numberOfRoomsLeftLimitSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("numberOfRoomsLeftLimit")
);

export const maxBookableRoomsSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("maxBookableRooms")
);

export const inventoryGroupingCriteriaSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("inventoryGroupingCriteria")
);

export const promoCodeSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("promoCode")
);

export const propertyTickersSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("propertyTickers")
);

export const activeFiltersSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("activeFilters")
);

export const addObligatoryExtrasRateToRoomSelectionSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("addObligatoryExtrasRateToRoomSelection")
);

export const unavailableURLSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("unavailableURL")
);

export const messageCodeSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("messageCode")
);

export const hotelSiteURLSelector = createSelector(configurationSelector, configuration => {
  if (configuration) {
    let hotelSiteUrl = configuration.get("hotelSiteUrl");
    if (
      hotelSiteUrl.indexOf("http://") === -1 &&
      hotelSiteUrl.indexOf("https://") === -1 &&
      hotelSiteUrl.indexOf("//") !== 0
    ) {
      hotelSiteUrl = "//" + hotelSiteUrl;
    }
    return hotelSiteUrl;
  }
});

export const showPromosSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("showPromos")
);

export const showDiscountsCalculationSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("showDiscountsCalculation")
);

export const showInactiveDiscountsSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("showInactiveDiscounts")
);

export const showRestrictedHighlightMessagesSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("showRestrictedHighlightMessages")
);

export const deviceInfoSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("deviceInfo")
);

export const propertyDataSelector = createSelector(
  getModelSelector,
  model => model && model.get(constants.PROPERTY_DATA_KEY)
);

export const propertyARISelector = createSelector(
  getModelSelector,
  model => model && model.get(constants.PROPERTY_ARI_KEY)
);

export const propertyARIAllSelector = createSelector(
  getModelSelector,
  model => model && model.get(constants.PROPERTY_ARI_ALL_KEY)
);

export const inventoriesSelector = createSelector(
  propertyARISelector,
  propertyTickerSelector,
  (propertyARI, propertyTicker) => {
    return (
      propertyARI &&
      propertyARI.get(propertyTicker) &&
      propertyARI.get(propertyTicker).get("inventories")
    );
  }
);

export const servicesSelector = createSelector(
  propertyARISelector,
  propertyTickerSelector,
  (propertyARI, propertyTicker) => {
    return (
      propertyARI &&
      propertyARI.get(propertyTicker) &&
      propertyARI.get(propertyTicker).get("services")
    );
  }
);

export const defaultCurrencySelector = createSelector(
  propertyTickerSelector,
  propertyDataSelector,
  configurationSelector,
  (propertyTicker, propertyData, configuration) =>
    (propertyTicker &&
      propertyData &&
      propertyData.get(propertyTicker) &&
      propertyData.get(propertyTicker).get("defaultCurrency")) ||
    (configuration && configuration.get("defaultCurrency"))
);

export const promotionsSelector = createSelector(
  propertyARISelector,
  propertyDataSelector,
  currencySelector,
  currencyExchangeSelector,
  showInactiveDiscountsSelector,
  (propertyARI, propertyData, currency, currencyExchange, showInactiveDiscounts) => {
    /* TODO: document and make this code clearer and cleaner */
    if (propertyARI && propertyARI.size > 0 && propertyData && propertyData.size > 0) {
      const merged = propertyARI.map(ARI => propertyData.get(ARI.get("ticker")).merge(ARI));
      const bookableTypes = ["discounts"];
      let discountsByInventory = new Set();
      const propertyDiscounts = merged.map(property => {
        bookableTypes.forEach(type => {
          if (!showInactiveDiscounts && property.get("inventories")) {
            property.get("inventories").map(bookable => {
              const inventory = bookable;
              if (inventory.get("discountTickers") && inventory.get("discountTickers").size > 0) {
                discountsByInventory = discountsByInventory.concat(
                  inventory.get("discountTickers")
                );
              }
            });
          }

          property = property.set(
            type, // eslint-disable-line
            property
              .get(type)
              .map(bookable => {
                let discount = bookable;
                if (!showInactiveDiscounts) {
                  const isDiscountActive = discount
                    .get("status")
                    .reduce(
                      (prev, val) =>
                        prev &&
                        val.get("code") &&
                        val.get("code") === constants.BOOKABLE_STATUS.AVAILABLE,
                      true
                    );
                  if (!isDiscountActive) {
                    return null;
                  }
                  // If property is not a chain and it's a discount which applies a reduction but it is not associated with any of the current inventories, then do not show it
                  if (
                    property.get("type") !== constants.PROPERTY_TYPE.CHAIN &&
                    !discountsByInventory.has(discount.get("ticker"))
                  ) {
                    return null;
                  }
                }
                if (
                  !discount.get("percentage") &&
                  currency !== currencyExchange.get("currencyBase") &&
                  currencyExchange.get("prices").get(currency)
                ) {
                  discount = discount.set(
                    "reduction",
                    discount.get("reduction") * currencyExchange.get("prices").get(currency)
                  );
                }
                return property.get("arivalues").get(discount.get("ticker"))
                  ? property.get("arivalues").get(discount.get("ticker")).merge(discount)
                  : discount;
              })
              .filter(elem => elem !== null)
          );
        });
        property = property.get("arivalues").get(property.get("ticker")).merge(property); // eslint-disable-line
        return property.get("discounts");
      });
      return propertyDiscounts;
    }
  }
);

export const showElementsConfigSelector = createSelector(
  configurationSelector,
  promotionsSelector,
  propertyTickerSelector,
  (configuration, promotions, propertyTicker) => {
    const result = {};
    if (configuration) {
      result.showDesktopLogo = configuration.get("showDesktopLogo");
      result.showMobileLogo = configuration.get("showMobileLogo");
      result.showCurrencySelector = configuration.get("showCurrencySelector");
      result.showLanguageSelector = configuration.get("showLanguageSelector");
      result.showBookingForm = configuration.get("showBookingForm");
      result.showPromos =
        configuration.get("showPromos") &&
        promotions &&
        promotions.get(propertyTicker) &&
        promotions.get(propertyTicker).size > 0;
      result.showPropertyMap = configuration.get("showPropertyMap");
      result.showMenu = configuration.get("showMenu");
    }
    return result;
  }
);

export const isPropertyAvailableSelector = createSelector(propertyARISelector, propertyARI => {
  if (propertyARI && propertyARI.size > 0) {
    return propertyARI
      .toList()
      .filter(ARI => ARI.get("type") !== constants.PROPERTY_TYPE.CHAIN)
      .reduce((accumulator, property) => {
        accumulator =
          accumulator ||
          (property && // eslint-disable-line
            property.get("arivalues") &&
            property.get("arivalues").get(property.get("ticker")) &&
            property.get("arivalues").get(property.get("ticker")).get("lastStatus") &&
            (property.get("arivalues").get(property.get("ticker")).get("lastStatus").get("code") ===
              constants.BOOKABLE_STATUS.AVAILABLE || // eslint-disable-line
              property
                .get("arivalues")
                .get(property.get("ticker"))
                .get("lastStatus")
                .get("code") === constants.BOOKABLE_STATUS.RESTRICTED)); // eslint-disable-line
        return accumulator;
      }, false);
  }
  return false;
});

export const messagesIdSelector = createSelector(
  propertyTickerSelector,
  propertyDataSelector,
  isPropertyAvailableSelector,
  messageCodeSelector,
  submittedPromoCodesSelector,
  (propertyTicker, propertyData, isPropertyAvailable, messageCode, promoCodes) => {
    const messages =
      propertyData &&
      propertyData.get(propertyTicker) &&
      propertyData.get(propertyTicker).get("messages");

    if (!messages) {
      return null;
    }
    // TODO: Check if shows only when has not availability
    let result = new Map();
    const resultString = [];
    const closedMessages = getFromSessionStorage("closedMessages") || {};
    if (!closedMessages[propertyTicker]) {
      closedMessages[propertyTicker] = {};
    }
    messages.forEach(item => {
      if (
        !closedMessages[propertyTicker][item.get("position")] ||
        !closedMessages[propertyTicker][item.get("position")][item.get("id")]
      ) {
        if (isPropertyAvailable && item.get("showOnUnavailable")) {
          return;
        }
        if (item.get("code")) {
          if (!messageCode && !promoCodes) {
            return;
          }
          const messageCodes = messageCode ? messageCode.split(",") : [];
          const currentPromoCodes = promoCodes ? promoCodes.split(",") : [];
          const givenCodes = new Set([...currentPromoCodes, ...messageCodes]);
          const itemCodes = new Set([...item.get("code").split(",")]);
          const intersection = givenCodes.filter(code => itemCodes.has(code));
          const isCodeValid = intersection.size > 0;
          if (!isCodeValid) {
            return;
          }
        }
        if (!result.get(item.get("position"))) {
          result = result.set(item.get("position"), new List());
        }
        result = result.set(item.get("position"), result.get(item.get("position")).push(item));
        resultString.push(item.get("id"));
      }
    });
    resultString.sort();
    return resultString.join(",");
  }
);

export const messagesSelector = createSelector(
  messagesIdSelector,
  propertyTickerSelector,
  propertyDataSelector,
  restoredSessionSelector,
  (messagesIds, propertyTicker, propertyData, restoredSession) => {
    if (!messagesIds || messagesIds.length <= 0) {
      return null;
    }
    let messages =
      propertyData &&
      propertyData.get(propertyTicker) &&
      propertyData.get(propertyTicker).get("messages");
    const idsArr = messagesIds.split(",");
    if (idsArr.indexOf(constants.SPECIAL_MESSAGE_TYPES.OCCUPATION_MESSAGE) >= 0) {
      const occupationMessage = fromJS({
        id: constants.SPECIAL_MESSAGE_TYPES.OCCUPATION_MESSAGE,
        content: constants.SPECIAL_MESSAGE_TYPES.OCCUPATION_MESSAGE,
        position: "TOP_INVENTORY_STEP_1",
        type: "WARNING",
        visible: true,
        showOnUnavailable: false,
        validSince: "2016-09-01",
        validUntil: "2025-09-30",
      });
      messages = messages ? messages.push(occupationMessage) : new List().push(occupationMessage);
    }
    if (!messages) {
      return null;
    }
    let result = new Map();

    messages.forEach(item => {
      if (restoredSession) {
        item = item.set(
          "content",
          item
            .get("content")
            .replace(
              new RegExp(constants.MARKUP_TAG.GUEST_NAME, "g"),
              get(restoredSession, "guestData.name")
            )
            .replace(
              new RegExp(constants.MARKUP_TAG.GUEST_SURNAME, "g"),
              get(restoredSession, "guestData.surname")
            )
            .replace(
              new RegExp(constants.MARKUP_TAG.GUEST_COUNTRY_CODE, "g"),
              get(restoredSession, "guestData.countryCode")
            )
            .replace(
              new RegExp(constants.MARKUP_TAG.GUEST_PHONE_COUNTRY_CODE, "g"),
              get(restoredSession, "guestData.phoneCountryCode")
            )
            .replace(
              new RegExp(constants.MARKUP_TAG.GUEST_PHONE, "g"),
              get(restoredSession, "guestData.phone")
            )
            .replace(
              new RegExp(constants.MARKUP_TAG.GUEST_EMAIL, "g"),
              get(restoredSession, "guestData.email")
            )
            .replace(
              new RegExp(constants.MARKUP_TAG.GUEST_ADDRESS, "g"),
              get(restoredSession, "guestData.address")
            )
            .replace(
              new RegExp(constants.MARKUP_TAG.GUEST_ID_DOCUMENT, "g"),
              get(restoredSession, "guestData.idDocument")
            )
            .replace(
              new RegExp(constants.MARKUP_TAG.GUEST_ARRIVAL_TIME, "g"),
              get(restoredSession, "guestData.arrivalTime")
            )
            .replace(
              new RegExp(constants.MARKUP_TAG.GUEST_COMMENTS, "g"),
              get(restoredSession, "guestData.comments")
            )
        );
      }
      // We need to do this, because we are using the string representation of the ids
      const messageIdString = "" + item.get("id");
      if (idsArr.indexOf(messageIdString) >= 0) {
        if (!result.get(item.get("position"))) {
          result = result.set(item.get("position"), new List());
        }
        result = result.set(item.get("position"), result.get(item.get("position")).push(item));
      }
    });
    return result;
  }
);

export const markupsSelector = createSelector(
  propertyTickerSelector,
  propertyDataSelector,
  configurationSelector,
  (propertyTicker, propertyData, configuration) =>
    (propertyTicker &&
      propertyData &&
      propertyData.get(propertyTicker) &&
      propertyData.get(propertyTicker).get("markups")) ||
    (configuration && configuration.get("markups"))
);

export const menuMarkupSelector = createSelector(markupsSelector, markups => {
  const markupsAll = markups && markups.get(constants.MARKUP_PHASE.ALL);
  const menuMarkup =
    markupsAll && markupsAll.filter(markup => markup.get("name") === constants.MENU_MARKUP);

  if (menuMarkup && menuMarkup.get(0) && menuMarkup.get(0).get("active")) {
    return menuMarkup.get(0);
  }

  return null;
});

export const menuButtonMarkupSelector = createSelector(markupsSelector, markups => {
  const markupsAll = markups && markups.get(constants.MARKUP_PHASE.ALL);
  const menuMarkup =
    markupsAll && markupsAll.filter(markup => markup.get("name") === constants.MENU_BUTTON_MARKUP);

  if (menuMarkup && menuMarkup.get(0) && menuMarkup.get(0).get("active")) {
    return menuMarkup.get(0);
  }

  return null;
});

export const customStylesSelector = createSelector(configurationSelector, configuration => {
  let result = new Map();

  if (configuration) {
    if (configuration.get("body_background_color")) {
      result = result.set("bodyBackgroundColor", configuration.get("body_background_color"));
    }

    if (configuration.get("body_line_separator_color")) {
      result = result.set(
        "promotionsSeparatorColor",
        configuration.get("body_line_separator_color")
      );
    }

    if (configuration.get("booking_form_background_color")) {
      result = result.set(
        "bookingFormBackgroundColor",
        configuration.get("booking_form_background_color")
      );

      if (configuration.get("booking_form_background_color_opposite")) {
        result = result.set(
          "bookingFormOppositeColor",
          configuration.get("booking_form_background_color_opposite")
        );
      }
    } else {
      result = result.set("bookingFormBackgroundColor", "000000");
      result = result.set("bookingFormOppositeColor", "ffffff");
    }

    if (configuration.get("booking_form_button_background_color")) {
      result = result.set(
        "bookingFormButtonBackgroundColor",
        configuration.get("booking_form_button_background_color")
      );
    } else {
      result = result.set("bookingFormButtonBackgroundColor", "458e3f");
    }

    if (configuration.get("booking_form_button_color_opposite")) {
      result = result.set(
        "bookingFormButtonOppositeColor",
        configuration.get("booking_form_button_color_opposite")
      );
    } else {
      result = result.set("bookingFormButtonOppositeColor", white);
    }

    if (configuration.get("localization_menu_inactive_text_color")) {
      result = result.set(
        "dropdownMenuColor",
        configuration.get("localization_menu_inactive_text_color")
      );
    }

    if (configuration.get("logo_background_color")) {
      result = result.set("logoBackgroundColor", configuration.get("logo_background_color"));
    }

    if (configuration.get("logo_container_height")) {
      result = result.set("logoPadding", configuration.get("logo_container_height"));
    }

    if (configuration.get("title_active_text_color")) {
      result = result.set("promotionsColor", configuration.get("title_active_text_color"));
    }

    if (configuration.get("headerLogoBackgroundColor")) {
      result = result.set(
        "headerLogoBackgroundColor",
        configuration.get("headerLogoBackgroundColor")
      );
    }
  }

  return result;
});

export const logosSelector = createSelector(
  propertyDataSelector,
  propertyTickerSelector,
  (propertyData, propertyTicker) => {
    const logo =
      propertyData &&
      propertyData.get(propertyTicker) &&
      propertyData.get(propertyTicker).get("logo");
    if (!logo) {
      return fromJS({
        logo: {
          url:
            "https://s3.eu-central-1.amazonaws.com/witbooking.content.user/demo_urbano/logo/2/0000.png",
        },
        logoMobile: {
          url:
            "https://s3.eu-central-1.amazonaws.com/witbooking.content.user/demo_urbano/logo/2/0000.png",
        },
      });
    }
    const logoMobile =
      (propertyData &&
        propertyData.get(propertyTicker) &&
        propertyData.get(propertyTicker).get("logoMobile")) ||
      logo;
    const result = new Map();
    return result.set("logo", logo).set("logoMobile", logoMobile);
  }
);

export const isPropertyChainSelector = createSelector(
  propertyTickerSelector,
  propertyARISelector,
  (propertyTicker, propertyARI) =>
    propertyTicker &&
    propertyARI &&
    propertyARI.get(propertyTicker) &&
    propertyARI.get(propertyTicker).get("type") === constants.PROPERTY_TYPE.CHAIN
);

export const expandedBookablesSelector = createSelector(propsSelector, props => {
  if (props && props.location) {
    const params = queryStringParse(props.location.search);
    return params && params.expand;
  } else {
    return undefined;
  }
});

export const entityTypeBookablesSelector = createSelector(propsSelector, props => {
  if (props && props.location) {
    const params = queryStringParse(props.location.search);
    return params && params.entityType;
  } else {
    return undefined;
  }
});

export const expandFirstPropertySelector = createSelector(
  propertyARISelector,
  propertyTickerSelector,
  isPropertyChainSelector,
  expandedBookablesSelector,
  (propertyARI, propertyTicker, isPropertyChain, expandedBookables) => {
    if (expandedBookables) {
      const expandedProperties = expandedBookables.split(";");

      if (expandedProperties[0]) {
        const [
          expandedPropertyTicker,
          accommodationTicker,
          inventoryTicker,
        ] = expandedProperties[0].split(","); // eslint-disable-line
        if (propertyARI.get(expandedPropertyTicker)) {
          if (accommodationTicker) {
            if (
              propertyARI.get(expandedPropertyTicker).get("accommodations") &&
              propertyARI.get(expandedPropertyTicker).get("accommodations").get(accommodationTicker)
            ) {
              return false;
            }
          }
        }
      }
    }
    if (!isPropertyChain) {
      return propertyTicker;
    }
    if (propertyARI && propertyARI.size > 0) {
      const children = propertyARI.filter(ARI => ARI.get("type") !== constants.PROPERTY_TYPE.CHAIN);
      return children && children.size === 1
        ? (children && children.first().get("ticker")) || false
        : false;
    }
  }
);

export const displayElementUrlParamSelector = createSelector(propsSelector, props => {
  if (props && props.location) {
    const params = queryStringParse(props.location.search);
    return params && params.displayElement;
  } else {
    return undefined;
  }
});

function getReservationDataHidePrice(reservation, totalServiceRate) {
  reservation = reservation.setIn(["subTotalAmount", "amount"], 0);
  reservation = reservation.setIn(["totalAmount", "amount"], totalServiceRate);
  return reservation;
}

/* TODO: CHECK IF THE PROPERTY IS ACTIVE!!! */
export const reservationSelector = createSelector(
  getModelSelector,
  addObligatoryExtrasRateToRoomSelectionSelector,
  (model, addObligatoryExtrasRateToRoomSelection) => {
    if (!model || !model.get("reservation")) {
      return null;
    }
    let reservation = model.get("reservation");
    const hidePrice = reservation.get("hidePrice");
    let totalServiceRate = 0;
    /* TODO: CHECK IF THE PROPERTY IS ACTIVE!!! */
    const alteredRoomStays = reservation.get("roomReservations").map(roomStay => {
      let obligatoryServicesAmount = 0;
      let alteredDailyValues = [];
      if (addObligatoryExtrasRateToRoomSelection) {
        roomStay.get("selectedServices").forEach(service => {
          if (service.get("obligatory") && !service.get("percentage")) {
            obligatoryServicesAmount += service.get("amount").get("amount");
          } else if (!service.get("obligatory")) {
            totalServiceRate += service.get("amount").get("amount");
          }
        });
      }
      // add to subtotal
      roomStay = roomStay.setIn(
        ["subTotalAmount", "amount"],
        hidePrice
          ? obligatoryServicesAmount
          : roomStay.getIn(["subTotalAmount", "amount"]) + obligatoryServicesAmount
      );

      roomStay = roomStay.setIn(
        ["totalAmount", "amount"],
        hidePrice ? totalServiceRate : roomStay.getIn(["totalAmount", "amount"])
      );

      // alter discounts percentage
      const alteredDiscounts = roomStay.get("discounts").map(discount => {
        const discountAmount =
          (discount.get("totalDiscount") && discount.get("totalDiscount").get("amount")) || 0;
        const subTotalAmount = roomStay.getIn(["subTotalAmount", "amount"]);
        discount = hidePrice
          ? discount.set("value", 0)
          : discount.set("value", ((discountAmount / subTotalAmount) * 100).toFixed(2));
        if (hidePrice && discount.get("totalDiscount")) {
          discount = discount.setIn(["totalDiscount", "amount"], 0);
        }
        return discount;
      });
      roomStay = roomStay.set("discounts", alteredDiscounts);

      // HidePrice works hiding accommodation prices and only show extras prices
      if (addObligatoryExtrasRateToRoomSelection) {
        // add to dailyValues
        const numberOfNights = roomStay.get("dailyValues").size;

        const amountPerNight = obligatoryServicesAmount / numberOfNights;
        let stayAmount = 0;

        alteredDailyValues = roomStay.get("dailyValues").map(dailyValue => {
          stayAmount += dailyValue.get("amount");
          return dailyValue.update("amount", value =>
            hidePrice ? amountPerNight : value + amountPerNight
          );
        });
      } else {
        alteredDailyValues = roomStay.get("dailyValues").map(dailyValue => {
          return dailyValue.update("amount", value => (hidePrice ? 0 : value));
        });
      }

      roomStay = roomStay.set("dailyValues", alteredDailyValues);
      if (hidePrice) {
        const guaranteeAmount = roomStay.getIn(["guaranteeAmount", "amount"]);
        roomStay = roomStay.setIn(["guaranteeAmount", "amount"], hidePrice ? 0 : guaranteeAmount);
      }
      return roomStay;
    });
    reservation = reservation.set("roomReservations", alteredRoomStays);
    if (hidePrice) {
      return getReservationDataHidePrice(reservation, totalServiceRate);
    }
    return reservation;
  }
);

export const countriesSelector = createSelector(getModelSelector, model => {
  return (
    model && model.get("countries") && model.get("countries").sortBy(country => country.get("name"))
  );
});
/* TODO: this selector should not get evaluated in STEP_ONE Or STEP THREE */
export const propertyContactSelector = createSelector(
  propertyTickerSelector,
  propertyDataSelector,
  countriesSelector,
  (propertyTicker, propertyData, countries) => {
    if (
      propertyTicker &&
      propertyData &&
      propertyData.get(propertyTicker) &&
      propertyData.get(propertyTicker).get("contact")
    ) {
      let result = propertyData.get(propertyTicker).get("contact");
      if (countries && result.get("country")) {
        const propertyCountry = countries.find(
          country => country.get("code") === result.get("country")
        );
        if (propertyCountry) {
          result = result.set("country", propertyCountry.get("name"));
        }
      }
      if (propertyData.get(propertyTicker).get("description")) {
        result = result.set("description", propertyData.get(propertyTicker).get("description"));
      }
      return result;
    }
  }
);

export const propertyNameSelector = createSelector(
  propertyTickerSelector,
  propertyDataSelector,
  (propertyTicker, propertyData) =>
    propertyTicker &&
    propertyData &&
    propertyData.get(propertyTicker) &&
    propertyData.get(propertyTicker).get("name")
);

export const customPropertyNameSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("customPropertyName")
);

export const creditCardTypesSelector = createSelector(
  propertyDataSelector,
  propertyTickerSelector,
  (propertyData, propertyTicker) => {
    return (
      propertyData &&
      propertyTicker &&
      propertyData.get(propertyTicker) &&
      propertyData.get(propertyTicker).get("creditCardTypes")
    );
  }
);

export const mediaSelector = createSelector(
  propertyTickerSelector,
  propertyDataSelector,
  (propertyTicker, propertyData) =>
    propertyTicker &&
    propertyData &&
    propertyData.get(propertyTicker) &&
    propertyData.get(propertyTicker).get("media")
);

export const cancelReleaseSelector = createSelector(configurationSelector, configuration => {
  return configuration && configuration.get("cancellationRelease");
});

export const taxesPercentageSelector = createSelector(configurationSelector, configuration => {
  return configuration && configuration.get("step1WithoutTaxes");
});

function convertRoomStay(roomStay) {
  const amount = roomStay.get("totalAmount").get("amount");
  const nights = roomStay.get("nights");
  const rooms = roomStay.get("quantity");
  return {
    id: roomStay.get("confirmationId"),
    roomType: `${roomStay.get("accommodationType")}, ${roomStay.get(
      "roomConfiguration"
    )}, ${roomStay.get("mealPlan")}, ${roomStay.get("condition").get("name")}`,
    amount: amount,
    amountPerNight: amount / nights,
    nights: nights,
    rooms: rooms,
    roomsNight: rooms * nights,
    people: roomStay.get("capacity"),
    checkIn: roomStay.get("checkIn"),
    checkOut: roomStay.get("checkOut"),
    accommodationTicker: roomStay.get("accommodationTicker"),
  };
}

export const externalReservationSelector = createSelector(reservationSelector, reservation => {
  if (reservation) {
    const result = reservation.get("roomReservations").reduce(
      (accumulator, roomStay) => {
        accumulator.id = roomStay.get("confirmationId");
        accumulator.status = roomStay.get("status");
        accumulator.amount += roomStay.get("totalAmount").get("amount");
        accumulator.totalNights += roomStay.get("nights");
        accumulator.items.push(convertRoomStay(roomStay));
        return accumulator;
      },
      { id: "", amount: 0, totalNights: 0, items: [] }
    );
    result.id = reservation.get("id");
    return result;
  }
  return null;
});

export const gaDataSelector = createSelector(configurationSelector, configuration => {
  if (configuration) {
    return {
      gaCodeNumberWitbooking: configuration.get("googleAnalyticsWitbookingCode"),
      gaCodeNumber: configuration.get("googleanalyticscodenumber"),
      gaDomains: configuration.get("googleanalyticsdomains"),
    };
  }
  return null;
});

export const propertyIsDemoSelector = createSelector(
  configurationSelector,
  configuration =>
    configuration &&
    configuration.get("propertyStatus") &&
    configuration.get("propertyStatus") !== "live"
);
// merge propertyData with propertyARI
function mergePropertyDataWithPropertyARI({
  propertyARI,
  propertyData,
  propertyTicker,
  isPropertyChain,
}) {
  let merged = propertyARI
    .filter(ARI => ARI.get("type") !== constants.PROPERTY_TYPE.CHAIN)
    .map(ARI => propertyData.get(ARI.get("ticker")).merge(ARI));
  if (!isPropertyChain) {
    merged = merged.filter(property => property.get("ticker") === propertyTicker);
  }
  return merged;
}
// apply currency exchange multiplier to bookables
function applyCurrencyExchangeMultiplier({ mergedProperty, currency, currencyExchange }) {
  const bookableTypes = ["accommodations", "inventories", "services", "packages"];
  const bookablesWithARI = mergedProperty.map(property => {
    bookableTypes.forEach(type => {
      property = property.set(
        type, // eslint-disable-line
        property.get(type) &&
          property.get(type).map(bookable => {
            let bookableTicker = bookable.get("ticker");
            const accommodationTicker = "accommodation_" + bookable.get("ticker");
            const packageTicker = "package_" + bookable.get("ticker");

            if (type === "accommodations" && property.get("arivalues").get(accommodationTicker)) {
              bookableTicker = accommodationTicker;
            } else if (type === "packages" && property.get("arivalues").get(packageTicker)) {
              bookableTicker = packageTicker;
            }
            return property.get("arivalues").get(bookableTicker)
              ? transformBookableWithCurrencyConversion(
                  property.get("arivalues").get(bookableTicker).merge(bookable),
                  currency,
                  currencyExchange
                )
              : bookable;
          })
      );
    });
    property = transformBookableWithCurrencyConversion(
      property.get("arivalues").get(property.get("ticker")).merge(property),
      currency,
      currencyExchange
    ); // eslint-disable-line
    // Arivalues always have the default currency it's necessary to set the selected currency
    property = property.set("currency", currency);
    return property;
  });
  return bookablesWithARI;
}
// if there are reservations in cart we must apply substractions and status changes accordingly
function diminishAvailabilityAccordingToReservations({
  reservations,
  bookablesWithARI,
  currentStartDate,
  currentEndDate,
  propertyARI,
  propertyData,
}) {
  const accommodationsToCheck = {};
  const propertiesToCheck = {};
  let modifiedProperty = bookablesWithARI;
  reservations.map((propertyReservations, ticker) => {
    propertyReservations.map(reservation => {
      const reservationStart = moment(reservation.get("startDate")).startOf("day").toDate();
      const reservationEnd = moment(reservation.get("endDate")).startOf("day").toDate();
      const ariStart = moment(currentStartDate).startOf("day").toDate();
      const ariEnd = moment(currentEndDate).startOf("day").toDate();
      const inventoryTicker = reservation.get("inventory");

      const areReservationDaysIntersectingWithARIDays =
        reservationEnd > ariStart && reservationStart < ariEnd;
      if (areReservationDaysIntersectingWithARIDays) {
        const propertyTicker = reservation.get("propertyTicker");
        propertiesToCheck[propertyTicker] = propertyTicker;
        const inventoryRelations = propertyData.get(propertyTicker).get("inventoryRelations");
        const parent = inventoryRelations.get("parents").get(inventoryTicker);
        // get all siblings of the selected inventory
        const children = inventoryRelations.get("children").get(parent);
        // loop through all siblings
        children.map(child => {
          propertyARI
            .get(propertyTicker)
            .get("accommodations")
            .map(accommodation => {
              // diminish availability for all accommodations of each siblings of the selected inventory
              if (accommodation.get("inventories").indexOf(child) >= 0) {
                accommodationsToCheck[accommodation.get("ticker")] = {
                  accommodation: accommodation.get("ticker"),
                  propertyTicker,
                };
              }
            });
        });
        children.map(child => {
          const availability =
            modifiedProperty.get(ticker).get("inventories").get(child) &&
            modifiedProperty.get(ticker).get("inventories").get(child).get("availability");
          if (!availability) {
            return null;
          }
          const remainingAvailability = availability - reservation.get("numberOfUnits");
          if (remainingAvailability <= 0) {
            modifiedProperty = modifiedProperty.setIn(
              [ticker, "inventories", child, "lastStatus", "code"],
              constants.BOOKABLE_STATUS.UNAVAILABLE
            );
          }
          modifiedProperty = modifiedProperty.setIn(
            [ticker, "inventories", child, "availability"],
            remainingAvailability
          );
        });
      }
    });
  });
  // TODO: SIMPLIFY DUPLICATED CODE WITH THE CODE BLOCK BELOW THIS ONE
  Object.values(accommodationsToCheck).map(({ accommodation, propertyTicker }) => {
    // eslint-disable-line
    const relatedByAccommodation = propertyARI
      .get(propertyTicker)
      .get("accommodations")
      .get(accommodation)
      .get("inventories");
    let presentInventories = 0;
    let notAvailableInventories = 0;
    relatedByAccommodation.map(inventory => {
      const inventoryARI = modifiedProperty.get(propertyTicker).get("inventories").get(inventory);
      if (!inventoryARI) {
        return null;
      }
      presentInventories += 1;
      if (inventoryARI.getIn(["lastStatus", "code"]) !== constants.BOOKABLE_STATUS.AVAILABLE) {
        notAvailableInventories += 1;
      }
    });
    if (notAvailableInventories >= presentInventories) {
      modifiedProperty = modifiedProperty.setIn(
        [propertyTicker, "accommodations", accommodation, "lastStatus", "code"],
        constants.BOOKABLE_STATUS.UNAVAILABLE
      );
    }
  });
  // TODO: SIMPLIFY DUPLICATED CODE WITH THE ONE ABOVE
  Object.values(propertiesToCheck).map(propertyTicker => {
    // eslint-disable-line
    const accommodations = propertyARI.get(propertyTicker).get("accommodations");
    let presentAcommodations = 0;
    let notAvailableAccommodations = 0;
    accommodations.map(accommodation => {
      const accommodationARI = modifiedProperty
        .get(propertyTicker)
        .get("accommodations")
        .get(accommodation.get("ticker"));
      if (!accommodationARI) {
        return null;
      }
      presentAcommodations += 1;
      if (accommodationARI.getIn(["lastStatus", "code"]) !== constants.BOOKABLE_STATUS.AVAILABLE) {
        notAvailableAccommodations += 1;
      }
    });
    if (notAvailableAccommodations >= presentAcommodations) {
      modifiedProperty = modifiedProperty.setIn(
        [propertyTicker, "lastStatus", "code"],
        constants.BOOKABLE_STATUS.UNAVAILABLE
      );
    }
  });
  return modifiedProperty;
}
function calculateStayDays({ currentStartDate, currentEndDate }) {
  const ariStart = moment(currentStartDate).startOf("day").toDate();
  const ariEnd = moment(currentEndDate).startOf("day").toDate();
  return dateDiffInDays(ariStart, ariEnd);
}

function getMinimumRateForBookableWithChildren({
  modifiedProperty,
  propertyTicker,
  bookableTicker,
  type,
}) {
  const childrenType = type === "property" ? "accommodations" : "inventories";
  const children =
    type === "property"
      ? modifiedProperty.get(propertyTicker).get("accommodations")
      : modifiedProperty
          .get(propertyTicker)
          .get(childrenType)
          .filter(child => {
            const accommodationInventories = modifiedProperty
              .get(propertyTicker)
              .get("accommodations")
              .get(bookableTicker)
              .get("inventories");
            // We only analyze the inventories of this particular accommodation.
            return accommodationInventories.indexOf(child.get("ticker")) >= 0;
          });
  const minimumRate = children.reduce((accumulator, childARI) => {
    // const childARI = modifiedProperty.get(propertyTicker).get(childrenType).get(child);
    if (
      !childARI ||
      childARI.getIn(["lastStatus", "code"]) !== constants.BOOKABLE_STATUS.AVAILABLE
    ) {
      return accumulator;
    }
    if (accumulator === null) {
      return childARI.get("totalRate");
    }
    return accumulator > childARI.get("totalRate") ? childARI.get("totalRate") : accumulator;
  }, null);
  return minimumRate;
}

// if there are any obligatory extras associated with and inventory they will be added to the inventory rate.
// TODO: TAKE INTO CONSIDERATION DISCOUNT AMOUNTS
function addObligatoryExtrasToInventoryRate({
  bookablesWithARI,
  currentStartDate,
  currentEndDate,
  propertyARI,
  defaultCurrency,
  currencyExchange,
}) {
  let modifiedProperty = bookablesWithARI;
  modifiedProperty.map(property => {
    const propertyTicker = property.get("ticker");
    const inventories = modifiedProperty.get(propertyTicker).get("inventories");

    if (inventories === null || inventories.size <= 0) {
      return modifiedProperty;
    }
    // TODO: Check if this does not fail when there are no service in the property
    if (
      modifiedProperty.get(propertyTicker).get("services") === null ||
      modifiedProperty.get(propertyTicker).get("services").size <= 0
    ) {
      return modifiedProperty;
    }
    const obligatoryServices = modifiedProperty
      .get(propertyTicker)
      .get("services")
      .filter(service => service.get("obligatory"));
    if (obligatoryServices === null || obligatoryServices.size <= 0) {
      return modifiedProperty;
    }
    const days = calculateStayDays({ currentStartDate, currentEndDate });
    const accommodationsToCheck = {};
    const propertiesToCheck = {};
    inventories.map(inventory => {
      // eslint-disable-line
      const inventoryStatus = inventory.getIn(["lastStatus", "code"]);
      if (inventoryStatus !== constants.BOOKABLE_STATUS.AVAILABLE) {
        return;
      }
      const inventoryTicker = inventory.get("ticker");
      let negativeServicesTotalAmount = 0;
      const obligatoryServicesRate =
        (inventory.get("services") &&
          inventory
            .get("services")
            .filter(serviceTicker => obligatoryServices.has(serviceTicker))
            .map(serviceTicker => {
              const service = obligatoryServices.get(serviceTicker);
              const numberOfUnits = 1;
              const obligatoryExtraRate = calculateServiceRate({
                service,
                rooms: numberOfUnits,
                transformCurrency: true,
                stayRate: inventory.getIn(["originalRate"]),
                defaultCurrency,
                currencyExchange,
                inventoryTicker,
                propertyARI: propertyARI.get(propertyTicker),
              });

              if (obligatoryExtraRate < 0) {
                negativeServicesTotalAmount += obligatoryExtraRate;
              }

              return obligatoryExtraRate;
            })
            .reduce((accumulator, serviceRate) => accumulator + serviceRate, 0)) ||
        null;
      if (obligatoryServicesRate === null || obligatoryServicesRate === 0) {
        return;
      }
      const accommodation = inventory.get("accommodation");
      accommodationsToCheck[accommodation] = { accommodation, propertyTicker };
      propertiesToCheck[propertyTicker] = propertyTicker;
      const newInventoryRackRate =
        inventory.getIn(["rackRate"]) !== null && inventory.getIn(["rackRate"]) > 0
          ? inventory.getIn(["rackRate"]) + obligatoryServicesRate
          : 0;
      const newInventoryFirstNightCost =
        inventory.getIn(["firstNightCost"]) !== null && inventory.getIn(["firstNightCost"]) > 0
          ? inventory.getIn(["firstNightCost"]) + obligatoryServicesRate / days
          : 0;
      const newInventoryOriginalRate = inventory.getIn(["originalRate"]) + obligatoryServicesRate;
      const newInventoryTotalRate = inventory.getIn(["totalRate"]) + obligatoryServicesRate;
      const originalDiscountAmount = inventory.getIn(["discountAmount"]) || 0;
      const newInventoryDiscountAmount =
        originalDiscountAmount + Math.abs(negativeServicesTotalAmount);
      const newInventoryDiscountPercentage =
        newInventoryDiscountAmount /
        (newInventoryOriginalRate + Math.abs(negativeServicesTotalAmount));
      const newInventoryAverageRate = newInventoryTotalRate / days;
      modifiedProperty = modifiedProperty
        .setIn(
          [propertyTicker, "inventories", inventoryTicker, "discountAmount"],
          newInventoryDiscountAmount
        )
        .setIn(
          [propertyTicker, "inventories", inventoryTicker, "discountPercentage"],
          newInventoryDiscountPercentage
        )
        .setIn(
          [propertyTicker, "inventories", inventoryTicker, "averageRate"],
          newInventoryAverageRate
        )
        .setIn([propertyTicker, "inventories", inventoryTicker, "rackRate"], newInventoryRackRate)
        .setIn(
          [propertyTicker, "inventories", inventoryTicker, "firstNightCost"],
          newInventoryFirstNightCost
        )
        .setIn([propertyTicker, "inventories", inventoryTicker, "totalRate"], newInventoryTotalRate)
        .setIn(
          [propertyTicker, "inventories", inventoryTicker, "originalRate"],
          newInventoryOriginalRate
        );
    });
    Object.values(accommodationsToCheck).map(({ accommodation, propertyTicker }) => {
      // eslint-disable-line
      const minimumRate = getMinimumRateForBookableWithChildren({
        modifiedProperty,
        propertyTicker,
        bookableTicker: accommodation,
        type: "accommodation",
      });
      if (minimumRate !== null && minimumRate > 0) {
        modifiedProperty = modifiedProperty.setIn(
          [propertyTicker, "accommodations", accommodation, "totalRate"],
          minimumRate
        );
      }
    });
    Object.values(propertiesToCheck).map(propertyTicker => {
      // eslint-disable-line
      const minimumRate = getMinimumRateForBookableWithChildren({
        modifiedProperty,
        propertyTicker,
        bookableTicker: propertyTicker,
        type: "property",
      });
      if (minimumRate !== null && minimumRate > 0) {
        modifiedProperty = modifiedProperty.setIn([propertyTicker, "totalRate"], minimumRate);
      }
    });
  });

  return modifiedProperty;
}

function setCheapestBookableData(bookablesWithARI) {
  let result = bookablesWithARI;

  if (bookablesWithARI) {
    bookablesWithARI.map(property => {
      const cheapestBookable = property.get("cheapestBookable");

      const inventory =
        property.get("inventories") && property.get("inventories").get(cheapestBookable);

      const accommodationTicker = inventory && inventory.get("accommodation");

      const accommodationName =
        property.get("accommodations") &&
        property.get("accommodations").get(accommodationTicker) &&
        property.get("accommodations").get(accommodationTicker).get("name");

      const cheapestBookableData = {
        accommodationTicker,
        accommodationName,
        conditionTicker: inventory && inventory.get("condition"),
        configurationTicker: inventory && inventory.get("configuration"),
        mealplanTicker: inventory && inventory.get("mealplan"),
      };

      result = result.setIn([property.get("ticker"), "cheapestBookableData"], cheapestBookableData);
    });
  }

  return result;
}

function isRestrictionDifferentThanPropertyLastStatus(restriction, propertyLastStatus) {
  return (
    restriction.get("code") !== propertyLastStatus.get("code") ||
    restriction.get("value") !== propertyLastStatus.get("value") ||
    restriction.get("filterType") !== propertyLastStatus.get("filterType")
  );
}

function getInventoriesAndPropertyStatusByEntityType({
  property,
  entity,
  activeTab,
  propertyMinPrice,
  propertyLastStatus,
}) {
  const filteredInventories = {};
  const lastStatus = {};
  const minPrice = {};
  let isAvailableProperty = false;
  let isRestrictedProperty = false;
  let isMultipleRestrictionProperty = false;
  let propertyRestriction = null;
  const entityResult = entity
    .filter(accommodation => {
      let inventories = accommodation.get("inventories");
      if (activeTab.get("pattern")) {
        const activeTabFilter = activeTab.get("pattern").split(",");
        const activeTabFilterSize = activeTabFilter.length;
        let isAvailable = false;
        let isRestricted = false;
        let isMultipleRestriction = false;
        let restriction = null;
        inventories = inventories.filter(inventory => {
          let filtered = false;
          let index = 0;
          while (!filtered && index < activeTabFilterSize) {
            const filterRegExp = new RegExp(activeTabFilter[index], "g");
            if (filterRegExp.test(inventory)) {
              filtered = true;
              if (
                property.get("inventories").get(inventory) &&
                property.get("inventories").get(inventory).get("lastStatus")
              ) {
                switch (property.get("inventories").get(inventory).get("lastStatus").get("code")) {
                  case constants.PROPERTY_STATUS.AVAILABLE:
                    isAvailable = true;
                    isAvailableProperty = true;
                    lastStatus[accommodation.get("ticker")] = property
                      .get("inventories")
                      .get(inventory)
                      .get("lastStatus");
                    propertyLastStatus = lastStatus[accommodation.get("ticker")];
                    if (
                      !minPrice[accommodation.get("ticker")] ||
                      property.get("inventories").get(inventory).get("totalRate") <
                        minPrice[accommodation.get("ticker")]
                    ) {
                      minPrice[accommodation.get("ticker")] = property
                        .get("inventories")
                        .get(inventory)
                        .get("totalRate");
                    }
                    if (
                      !propertyMinPrice ||
                      propertyMinPrice > minPrice[accommodation.get("ticker")]
                    ) {
                      propertyMinPrice = minPrice[accommodation.get("ticker")];
                    }
                    break;

                  case constants.PROPERTY_STATUS.UNAVAILABLE:
                    if (!isAvailable && !isRestricted) {
                      lastStatus[accommodation.get("ticker")] = property
                        .get("inventories")
                        .get(inventory)
                        .get("lastStatus");
                    }
                    if (!isAvailableProperty && !isRestrictedProperty) {
                      propertyLastStatus = property
                        .get("inventories")
                        .get(inventory)
                        .get("lastStatus");
                    }
                    break;

                  case constants.PROPERTY_STATUS.RESTRICTED:
                    isRestricted = true;
                    isRestrictedProperty = true;
                    if (!isAvailable && !isMultipleRestriction) {
                      if (!restriction) {
                        restriction = property.get("inventories").get(inventory).get("lastStatus");
                      }
                      if (
                        isRestrictionDifferentThanPropertyLastStatus(
                          restriction,
                          property.get("inventories").get(inventory).get("lastStatus")
                        )
                      ) {
                        isMultipleRestriction = true;
                        lastStatus[accommodation.get("ticker")] = property
                          .get("inventories")
                          .get(inventory)
                          .get("lastStatus")
                          .set("value", null)
                          .set("filterType", constants.ARI_VALUE_TYPE.AVAILABILITY);
                      } else {
                        lastStatus[accommodation.get("ticker")] = property
                          .get("inventories")
                          .get(inventory)
                          .get("lastStatus");
                      }
                    }
                    if (!isAvailableProperty && !isMultipleRestrictionProperty) {
                      if (!propertyRestriction) {
                        propertyRestriction = property
                          .get("inventories")
                          .get(inventory)
                          .get("lastStatus");
                      }
                      if (
                        isRestrictionDifferentThanPropertyLastStatus(
                          propertyRestriction,
                          property.get("inventories").get(inventory).get("lastStatus")
                        )
                      ) {
                        isMultipleRestrictionProperty = true;
                        propertyLastStatus = property
                          .get("inventories")
                          .get(inventory)
                          .get("lastStatus")
                          .set("value", null)
                          .set("filterType", constants.ARI_VALUE_TYPE.AVAILABILITY);
                      } else {
                        propertyLastStatus = property
                          .get("inventories")
                          .get(inventory)
                          .get("lastStatus");
                      }
                    }
                    break;

                  default:
                    break;
                }
              }
            }
            index++;
          }
          return filtered;
        });
      }

      filteredInventories[accommodation.get("ticker")] = inventories;
      return inventories.size > 0;
    })
    .map(accommodation => {
      let accommodationFiltered = accommodation;
      accommodationFiltered = accommodationFiltered.set(
        "inventories",
        filteredInventories[accommodation.get("ticker")]
      );

      if (lastStatus[accommodation.get("ticker")]) {
        accommodationFiltered = accommodationFiltered.set(
          "lastStatus",
          lastStatus[accommodation.get("ticker")]
        );
      }
      if (minPrice[accommodation.get("ticker")]) {
        accommodationFiltered = accommodationFiltered.set(
          "totalRate",
          minPrice[accommodation.get("ticker")]
        );
      }

      return accommodationFiltered;
    });

  return {
    entity: entityResult,
    minPrice: propertyMinPrice,
    lastStatus: propertyLastStatus,
  };
}

function getPackagesAndPropertyStatusByEntityType({
  property,
  entity,
  activeTab,
  propertyMinPrice,
  propertyLastStatus,
}) {
  const filteredInventories = {};
  const lastStatus = {};
  const minPrice = {};
  let isAvailableProperty = false;
  let isRestrictedProperty = false;
  let isMultipleRestrictionProperty = false;
  let propertyRestriction = null;
  const entityResult = entity
    .filter(accommodation => {
      let inventories = accommodation.get("inventories");
      let filtered = false;
      if (activeTab.get("pattern")) {
        const activeTabFilter = activeTab.get("pattern").split(",");
        let isAvailable = false;
        let isRestricted = false;
        let isMultipleRestriction = false;
        let restriction = null;
        activeTabFilter.forEach(filter => {
          if (accommodation.get("ticker") === filter.trim()) {
            inventories.forEach(inventory => {
              if (
                property.get("inventories").get(inventory) &&
                property.get("inventories").get(inventory).get("lastStatus")
              ) {
                switch (property.get("inventories").get(inventory).get("lastStatus").get("code")) {
                  case constants.PROPERTY_STATUS.AVAILABLE:
                    isAvailable = true;
                    isAvailableProperty = true;
                    lastStatus[accommodation.get("ticker")] = property
                      .get("inventories")
                      .get(inventory)
                      .get("lastStatus");
                    propertyLastStatus = lastStatus[accommodation.get("ticker")];
                    if (
                      !minPrice[accommodation.get("ticker")] ||
                      property.get("inventories").get(inventory).get("totalRate") <
                        minPrice[accommodation.get("ticker")]
                    ) {
                      minPrice[accommodation.get("ticker")] = property
                        .get("inventories")
                        .get(inventory)
                        .get("totalRate");
                    }
                    if (
                      !propertyMinPrice ||
                      propertyMinPrice > minPrice[accommodation.get("ticker")]
                    ) {
                      propertyMinPrice = minPrice[accommodation.get("ticker")];
                    }
                    break;

                  case constants.PROPERTY_STATUS.UNAVAILABLE:
                    if (!isAvailable && !isRestricted) {
                      lastStatus[accommodation.get("ticker")] = property
                        .get("inventories")
                        .get(inventory)
                        .get("lastStatus");
                    }
                    if (!isAvailableProperty && !isRestrictedProperty) {
                      propertyLastStatus = property
                        .get("inventories")
                        .get(inventory)
                        .get("lastStatus");
                    }
                    break;

                  case constants.PROPERTY_STATUS.RESTRICTED:
                    isRestricted = true;
                    isRestrictedProperty = true;
                    if (!isAvailable && !isMultipleRestriction) {
                      if (!restriction) {
                        restriction = property.get("inventories").get(inventory).get("lastStatus");
                      }
                      if (
                        isRestrictionDifferentThanPropertyLastStatus(
                          restriction,
                          property.get("inventories").get(inventory).get("lastStatus")
                        )
                      ) {
                        isMultipleRestriction = true;
                        lastStatus[accommodation.get("ticker")] = property
                          .get("inventories")
                          .get(inventory)
                          .get("lastStatus")
                          .set("value", null)
                          .set("filterType", constants.ARI_VALUE_TYPE.AVAILABILITY);
                      } else {
                        lastStatus[accommodation.get("ticker")] = property
                          .get("inventories")
                          .get(inventory)
                          .get("lastStatus");
                      }
                    }
                    if (!isAvailableProperty && !isMultipleRestrictionProperty) {
                      if (!propertyRestriction) {
                        propertyRestriction = property
                          .get("inventories")
                          .get(inventory)
                          .get("lastStatus");
                      }
                      if (
                        isRestrictionDifferentThanPropertyLastStatus(
                          propertyRestriction,
                          property.get("inventories").get(inventory).get("lastStatus")
                        )
                      ) {
                        isMultipleRestrictionProperty = true;
                        propertyLastStatus = property
                          .get("inventories")
                          .get(inventory)
                          .get("lastStatus")
                          .set("value", null)
                          .set("filterType", constants.ARI_VALUE_TYPE.AVAILABILITY);
                      } else {
                        propertyLastStatus = property
                          .get("inventories")
                          .get(inventory)
                          .get("lastStatus");
                      }
                    }
                    break;

                  default:
                    break;
                }
              }
            });
            filteredInventories[accommodation.get("ticker")] = inventories;
            filtered = true;
          }
        });
      } else {
        filteredInventories[accommodation.get("ticker")] = inventories;
        filtered = true;
      }
      return filtered;
    })
    .map(accommodation => {
      let accommodationFiltered = accommodation;
      accommodationFiltered = accommodationFiltered.set(
        "inventories",
        filteredInventories[accommodation.get("ticker")]
      );

      if (lastStatus[accommodation.get("ticker")]) {
        accommodationFiltered = accommodationFiltered.set(
          "lastStatus",
          lastStatus[accommodation.get("ticker")]
        );
      }
      if (minPrice[accommodation.get("ticker")]) {
        accommodationFiltered = accommodationFiltered.set(
          "totalRate",
          minPrice[accommodation.get("ticker")]
        );
      }

      return accommodationFiltered;
    });

  return {
    entity: entityResult,
    minPrice: propertyMinPrice,
    lastStatus: propertyLastStatus,
  };
}

function filterInventoriesByTab(property, activeTab) {
  let propertyFiltered = property;
  let accommodations, packages;
  let propertyMinPrice = null,
    propertyLastStatus = null;
  if (
    property.get("accommodations") &&
    activeTab.get("type") !== constants.PROPERTY_FILTER.TYPE.PACKAGE
  ) {
    accommodations = getInventoriesAndPropertyStatusByEntityType({
      property,
      entity: property.get("accommodations"),
      activeTab,
      propertyMinPrice,
      propertyLastStatus,
    });
    propertyFiltered = propertyFiltered.set("accommodations", accommodations.entity);
    propertyMinPrice = accommodations.minPrice;
    propertyLastStatus = accommodations.lastStatus;
    if (property.get("packages")) {
      propertyFiltered = propertyFiltered.set("packages", undefined);
    }
  } else if (
    property.get("packages") &&
    activeTab.get("type") === constants.PROPERTY_FILTER.TYPE.PACKAGE
  ) {
    accommodations = getPackagesAndPropertyStatusByEntityType({
      property,
      entity: property.get("packages"),
      activeTab,
      propertyMinPrice,
      propertyLastStatus,
    });
    propertyFiltered = propertyFiltered.set("packages", accommodations.entity);
    propertyMinPrice = accommodations.minPrice;
    propertyLastStatus = accommodations.lastStatus;

    if (property.get("accommodations")) {
      propertyFiltered = propertyFiltered.set("accommodations", undefined);
    }
  }
  if ((accommodations && accommodations.size > 0) || (packages && packages.size > 0)) {
    if (propertyMinPrice) {
      propertyFiltered = propertyFiltered.set("totalRate", propertyMinPrice);
    }
    if (propertyLastStatus) {
      propertyFiltered = propertyFiltered.set("lastStatus", propertyLastStatus);
    }
  } else {
    propertyFiltered = propertyFiltered
      .set("totalRate", 0)
      .set(
        "lastStatus",
        new Map()
          .set("code", constants.PROPERTY_STATUS.UNAVAILABLE)
          .set("filterType", constants.ARI_VALUE_TYPE.AVAILABILITY)
      );
  }
  return propertyFiltered;
}

export const activePropertyTickerSelector = createSelector(
  getRoomSelectionModelSelector,
  model => model && model.get("activePropertyTicker")
);

export const propertyFiltersSelector = createSelector(
  propertyTickerSelector,
  propertyDataSelector,
  (propertyTicker, propertyData) =>
    propertyTicker &&
    propertyData &&
    propertyData.get(propertyTicker) &&
    propertyData.get(propertyTicker).get("filters").size > 0 &&
    propertyData.get(propertyTicker).get("filters")
);

export const propertyTabsSelector = createSelector(
  propertyTickerSelector,
  propertyDataSelector,
  propertyFiltersSelector,
  (propertyTicker, propertyData, propertyFilters) =>
    propertyFilters &&
    propertyFilters.filter(
      filter =>
        filter.get("representation") === constants.PROPERTY_FILTER.REPRESENTATION.TAB &&
        filter
          .get("options")
          .find(option => option.get("type") !== constants.PROPERTY_FILTER.TYPE.EXTRA)
    )
);

export const extraFiltersSelector = createSelector(
  propertyTickerSelector,
  propertyDataSelector,
  propertyFiltersSelector,
  (propertyTicker, propertyData, propertyFilters) => {
    return (
      propertyFilters &&
      propertyFilters.filter(
        filter =>
          filter.get("options").size > 0 &&
          filter
            .get("options")
            .find(option => option.get("type") === constants.PROPERTY_FILTER.TYPE.EXTRA)
      )
    );
  }
);
export const selectedTabSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("selectedTab")
);

export const propertyActiveTabSelector = createSelector(
  propertyTabsSelector,
  selectedTabSelector,
  (propertyTabs, selectedTab) => {
    if (!propertyTabs || !propertyTabs.get(0)) {
      return null;
    }

    if (selectedTab) {
      const activeTab =
        propertyTabs.get(0) &&
        propertyTabs.get(0).get("options") &&
        propertyTabs
          .get(0)
          .get("options")
          .filter(tab => tab.get("optionTicker") === selectedTab);
      if (activeTab) {
        return activeTab.get(0);
      }
    } else {
      return propertyTabs.get(0).get("options") && propertyTabs.get(0).get("options").get(0);
    }

    return null;
  }
);

function applyTaxPercentageReduction({ bookablesWithARI, taxesPercentage }) {
  let reversedTaxesPercentage = 1 / taxesPercentage; //eslint-disable-line
  let modifiedProperty = bookablesWithARI;
  modifiedProperty.map(property => {
    const propertyTicker = property.get("ticker");
    const inventories = modifiedProperty.get(propertyTicker).get("inventories");

    if (inventories === null || inventories.size <= 0) {
      return modifiedProperty;
    }
    inventories.map(inventory => {
      // eslint-disable-line
      const inventoryStatus = inventory.getIn(["lastStatus", "code"]);
      if (inventoryStatus !== constants.BOOKABLE_STATUS.AVAILABLE) {
        return;
      }
      const inventoryTicker = inventory.get("ticker");
      const newInventoryRackRate =
        inventory.getIn(["rackRate"]) !== null && inventory.getIn(["rackRate"]) > 0
          ? inventory.getIn(["rackRate"]) * reversedTaxesPercentage
          : 0;
      const newInventoryOriginalRate = inventory.getIn(["originalRate"]) * reversedTaxesPercentage;
      const newInventoryTotalRate = inventory.getIn(["totalRate"]) * reversedTaxesPercentage;
      const newInventoryAverageRate = inventory.getIn(["averageRate"]) * reversedTaxesPercentage;
      modifiedProperty = modifiedProperty
        .setIn([propertyTicker, "inventories", inventoryTicker, "rackRate"], newInventoryRackRate)
        .setIn(
          [propertyTicker, "inventories", inventoryTicker, "originalRate"],
          newInventoryOriginalRate
        )
        .setIn([propertyTicker, "inventories", inventoryTicker, "totalRate"], newInventoryTotalRate)
        .setIn(
          [propertyTicker, "inventories", inventoryTicker, "averageRate"],
          newInventoryAverageRate
        );
    });
    const accommodations = modifiedProperty.get(propertyTicker).get("accommodations");

    accommodations.map(accommodation => {
      // eslint-disable-line
      const accommodationTicker = accommodation.get("ticker");
      const rate =
        modifiedProperty.getIn([
          propertyTicker,
          "accommodations",
          accommodationTicker,
          "totalRate",
        ]) * reversedTaxesPercentage || 0;
      modifiedProperty = modifiedProperty.setIn(
        [propertyTicker, "accommodations", accommodationTicker, "totalRate"],
        rate
      );
    });

    const rate = modifiedProperty.getIn([propertyTicker, "totalRate"]) * reversedTaxesPercentage;
    modifiedProperty = modifiedProperty.setIn([propertyTicker, "totalRate"], rate);
  });
  return modifiedProperty;
}

export const paymentTypesConfigurationSelector = createSelector(
  propertyARISelector,
  propertyTickerSelector,
  (propertyARI, propertyTicker) => {
    if (propertyARI && propertyARI.size > 0) {
      let paymentTypes = new Map();

      propertyARI
        .get(propertyTicker)
        .get("conditions")
        .map(condition => {
          condition.get("paymentTypes").map(paymentType => {
            if (!paymentTypes.has(paymentType.get("ticker"))) {
              paymentTypes = paymentTypes.set(paymentType.get("ticker"), paymentType);
            }
          });
        });
      return paymentTypes;
    }
    return null;
  }
);

export const propertiesWithInvalidOccupationsSelector = createSelector(propertyARISelector, ARI => {
  let propertiesWithInvalidOccupations = new List();
  ARI.map((propertyARI, propertyTicker) => {
    propertyARI.get("status").map((status, type) => {
      if (
        type === constants.ARI_VALUE_TYPE.OCCUPATION &&
        status.get("code") === constants.BOOKABLE_STATUS.IGNORED_FILTER
      ) {
        propertiesWithInvalidOccupations = propertiesWithInvalidOccupations.push(propertyTicker);
      }
    });
  });
  return propertiesWithInvalidOccupations;
});

export function buildPropertyList(
  propertyARI,
  propertyData,
  reservations,
  currentStartDate,
  currentEndDate,
  defaultCurrency,
  currency,
  currencyExchange,
  propertyTicker,
  isPropertyChain,
  addObligatoryExtrasRateToRoomSelection,
  propertyTabs,
  configuration,
  taxesPercentage,
  propertyActiveTab
) {
  if (propertyARI && propertyARI.size > 0 && propertyData && propertyData.size > 0) {
    // merge propertyData with propertyARI
    const merged = mergePropertyDataWithPropertyARI({
      propertyARI,
      propertyData,
      propertyTicker,
      isPropertyChain,
    });
    // apply currency exchange multiplier to bookables
    let bookablesWithARI = applyCurrencyExchangeMultiplier({
      mergedProperty: merged,
      currency,
      currencyExchange,
    });

    if (taxesPercentage && taxesPercentage > 0) {
      bookablesWithARI = applyTaxPercentageReduction({ bookablesWithARI, taxesPercentage });
    }
    if (reservations) {
      bookablesWithARI = diminishAvailabilityAccordingToReservations({
        reservations,
        bookablesWithARI,
        currentStartDate,
        currentEndDate,
        propertyARI,
        propertyTicker,
        propertyData,
      });
    }

    if (addObligatoryExtrasRateToRoomSelection) {
      bookablesWithARI = addObligatoryExtrasToInventoryRate({
        reservations,
        bookablesWithARI,
        currentStartDate,
        currentEndDate,
        propertyARI,
        propertyTicker,
        propertyData,
        defaultCurrency,
        currencyExchange,
      });
    }

    bookablesWithARI = setCheapestBookableData(bookablesWithARI);

    // Filters by tab
    let bookablesWithARIFiltered = bookablesWithARI;
    if (propertyTabs && configuration) {
      if (propertyActiveTab) {
        bookablesWithARI.map(ARI => {
          bookablesWithARIFiltered = bookablesWithARIFiltered.set(
            propertyTicker,
            filterInventoriesByTab(ARI, propertyActiveTab)
          );
        });
      }
    }
    return bookablesWithARIFiltered
      .toList()
      .sort((itemA, itemB) =>
        ariElmentSortBy(itemA, itemB, "totalRate", ["lastStatus", "code"], false)
      );
  }
  return [];
}

export const propertyListSelector = createSelector(
  propertyARISelector,
  propertyDataSelector,
  reservationSelectors.reservationsSelector,
  startDateSelector,
  endDateSelector,
  defaultCurrencySelector,
  currencySelector,
  currencyExchangeSelector,
  propertyTickerSelector,
  isPropertyChainSelector,
  addObligatoryExtrasRateToRoomSelectionSelector,
  propertyTabsSelector,
  configurationSelector,
  taxesPercentageSelector,
  propertyActiveTabSelector,
  buildPropertyList
);

export const numberOfGuestsSelector = createSelector(configurationSelector, configuration => {
  // TODO: THIS SHOULD NOT WORK WHEN USING GUEST AGES
  let guests = 0;
  if (configuration) {
    for (const occupant in constants.OCCUPANTS_TYPE) {
      if (configuration.get(constants.OCCUPANTS_TYPE[occupant])) {
        guests += parseInt(configuration.get(constants.OCCUPANTS_TYPE[occupant]), 10);
      }
    }
  }
  return guests;
});

export const cheapestBookableDataSelector = createSelector(
  propertyTickerSelector,
  propertyListSelector,
  startDateSelector,
  endDateSelector,
  (propertyTicker, propertyList, startDate, endDate) => {
    const cheapestBookableData = {};

    if (propertyList) {
      const nights = dateDiffInDays(startDate, endDate);
      const cheapestRate = {};

      propertyList.map(property => {
        if (property.get("totalRate")) {
          const propertyCheapestRate =
            (property.get("totalRate") && property.get("totalRate") / nights) || 0;

          if (
            propertyCheapestRate < cheapestRate[property.get("ticker")] ||
            !cheapestRate[property.get("ticker")]
          ) {
            cheapestBookableData[property.get("ticker")] =
              property.get("cheapestBookableData") || {};
            cheapestBookableData[property.get("ticker")].cheapestRate = propertyCheapestRate;
            cheapestRate[property.get("ticker")] = propertyCheapestRate;
          }
        }
      });
    }

    return cheapestBookableData;
  }
);

export const childrenMaxAgeSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("childrenMaxAge")
);

export const occupantsSelector = createSelector(
  propertyARISelector,
  propertyTickerSelector,
  (propertyARI, propertyTicker) => propertyARI.get(propertyTicker).get("occupants")
);

export const externalConfigurationSelector = createSelector(
  configurationSelector,
  propertyListSelector,
  propertyTickerSelector,
  propertyNameSelector,
  currencySelector,
  defaultCurrencySelector,
  localeSelector,
  defaultLocaleSelector,
  startDateSelector,
  endDateSelector,
  isPropertyChainSelector,
  numberOfGuestsSelector,
  reservationSelectors.guestsListSelector,
  cheapestBookableDataSelector,
  childrenMaxAgeSelector,
  submittedPromoCodesSelector,
  occupantsSelector,
  (
    configuration,
    propertyList,
    propertyTicker,
    propertyName,
    currency,
    defaultCurrency,
    locale,
    defaultLocale,
    startDate,
    endDate,
    isPropertyChain,
    guests,
    guestsList,
    cheapestBookableData,
    childrenMaxAge,
    submittedPromoCodes,
    occupants
  ) => {
    const nights = dateDiffInDays(startDate, endDate);
    const cheapestRate = {};
    propertyList.map(property => {
      cheapestRate[property.get("ticker")] =
        (property.get("totalRate") && property.get("totalRate") / nights) || 0;
    });
    const result = {
      wbkHotelinkingConfiguration:
        configuration &&
        configuration.get("hotelinkingConfiguration") &&
        configuration.get("hotelinkingConfiguration").toJS(),
      establishment: propertyTicker,
      propertyTicker:
        (isPropertyChain &&
          configuration &&
          configuration.get("propertyTickers") &&
          configuration.get("propertyTickers").size === 1 &&
          configuration.get("propertyTickers").get(0)) ||
        null,
      propertyName,
      guests,
      currency,
      defaultCurrency,
      language: locale,
      startDate: startDate && moment(startDate).format("DD-MM-YYYY"),
      endDate: endDate && moment(endDate).format("DD-MM-YYYY"),
      cheapestRate,
      cheapestBookableData,
      nights,
      childrenMaxAge,
      promoCodes: submittedPromoCodes,
    };
    if (occupants) {
      result.guestList = {
        seniors: occupants.get(constants.CONFIGURATION_OCCUPANTS.SENIOR),
        adults: occupants.get(constants.CONFIGURATION_OCCUPANTS.ADULT),
        teenagers: occupants.get(constants.CONFIGURATION_OCCUPANTS.TEENAGER),
        children: occupants.get(constants.CONFIGURATION_OCCUPANTS.CHILD),
        babies: occupants.get(constants.CONFIGURATION_OCCUPANTS.BABY),
      };
    }

    return result;
  }
);

export const accommodationsOrderSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("accommodationsOrder")
);

export const isWithotelSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("withotel")
);

export const propertyValuationSelector = createSelector(
  propertyTickerSelector,
  propertyDataSelector,
  (propertyTicker, propertyData) =>
    propertyTicker &&
    propertyData &&
    propertyData.get(propertyTicker) &&
    propertyData.get(propertyTicker).get("valuation")
);

export const propertyDiscountsListSelector = createSelector(
  propertyARISelector,
  propertyTickerSelector,
  (propertyARI, propertyTicker) =>
    propertyARI &&
    propertyARI.get(propertyTicker) &&
    propertyARI.get(propertyTicker).get("discounts")
);

export const originSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("origin")
);

export const modeSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("mode")
);

export const bMindTrackerSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("bMindTracker")
);

export const comiNetoSelector = createSelector(
  configurationSelector,
  configuration =>
    configuration && configuration.get("comiNeto") && parseFloat(configuration.get("comiNeto"))
);

export const affiliateSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("affiliate")
);

export const encodedAffiliateSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("encodedAffiliate")
);

export const tripAdvisorDataLayerSelector = createSelector(
  configurationSelector,
  configuration =>
    configuration && {
      tacurrency: configuration.get("tacurrency"),
      tabr: configuration.get("tabr"),
      tatf: configuration.get("tatf"),
      tapi: configuration.get("tapi"),
    }
);

export const externalHotelIdSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("externalHotelId")
);

export const customOriginSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("customOrigin")
);

export const loyaltySelector = createSelector(
  propertyDataSelector,
  propertyTickerSelector,
  (propertyData, propertyTicker) => {
    const tier =
      propertyTicker &&
      propertyData &&
      propertyData.get(propertyTicker) &&
      propertyData.get(propertyTicker).get("tier");
    if (tier) {
      const loyalty = {
        color: tier.get("color"),
        ticker: tier.get("ticker"),
        name: tier.get("translations").get("name"),
        benefitDescription: tier.get("translations").get("benefitDescription"),
        description: tier.get("translations").get("description"),
        highlightedNameBottom: tier.get("translations").get("highlightedNameBottom"),
        highlightedNameTop: tier.get("translations").get("highlightedNameTop"),
        promotedDiscount: tier.get("promotedDiscount").toJS(),
      };
      loyalty.promotedDiscount = {
        ...loyalty.promotedDiscount,
        amount: loyalty.promotedDiscount.amount && loyalty.promotedDiscount.amount / 100,
      };
      return loyalty;
    }
    return null;
  }
);

export const brandSelector = createSelector(
  propertyDataSelector,
  propertyTickerSelector,
  (propertyData, propertyTicker) => {
    const brand =
      propertyTicker &&
      propertyData &&
      propertyData.get(propertyTicker) &&
      propertyData.get(propertyTicker).get("brand");
    if (brand) {
      return {
        name: brand.get("translations").get("name"),
        ticker: brand.get("ticker"),
        mainDomain: brand.get("mainDomain"),
        authorizationServiceDomain: brand.get("authorizationServiceDomain"),
      };
    }
    return null;
  }
);

export const loyaltyCustomerIdSelector = createSelector(
  restoredSessionSelector,
  restoredSession =>
    restoredSession && restoredSession.guestData && restoredSession.guestData.customerId
);

export const onlineStoreHidePriceSelector = createSelector(
  restoredSessionSelector,
  restoredSession => {
    return (restoredSession && restoredSession.hidePrice) || false;
  }
);

const calculateDepositStatus = (roomReservation, paymentMethod) => {
  const guaranteeAmount =
    roomReservation.get("guaranteeAmount") && roomReservation.get("guaranteeAmount").get("amount");
  const totalAmount =
    roomReservation.get("totalAmount") && roomReservation.get("totalAmount").get("amount");

  if (
    !guaranteeAmount ||
    paymentMethod === constants.PAYMENT_TYPES.CREDIT_CARD_VAULT ||
    paymentMethod === constants.PAYMENT_TYPES.TRANSFER ||
    paymentMethod === constants.PAYMENT_TYPES.WITHOUT_CARD ||
    paymentMethod === constants.PAYMENT_TYPES.RESERVATION_REQUEST ||
    paymentMethod === constants.PAYMENT_TYPES.DEFERRED
  ) {
    return constants.DEPOSIT_STATUS.NONE;
  }
  if (totalAmount > guaranteeAmount) {
    return constants.DEPOSIT_STATUS.PRE_PAY;
  }
  if (totalAmount === guaranteeAmount) {
    return constants.DEPOSIT_STATUS.PAID;
  }
  return constants.DEPOSIT_STATUS.NONE;
};

export const wbkDataLayerSelector = createSelector(
  configurationSelector,
  localeSelector,
  propertyTickerSelector,
  propertyNameSelector,
  propertyValuationSelector,
  reservationSelector,
  defaultCurrencySelector,
  propertyContactSelector,
  reservationSelectors.cartSelector,
  isPropertyAvailableSelector,
  propertyDiscountsListSelector,
  originSelector,
  customPropertyNameSelector,
  comiNetoSelector,
  propertyARISelector,
  cheapestBookableDataSelector,
  affiliateSelector,
  tripAdvisorDataLayerSelector,
  externalHotelIdSelector,
  propertyListSelector,
  customOriginSelector,
  loyaltySelector,
  brandSelector,
  loyaltyCustomerIdSelector,
  modeSelector,
  (
    configuration,
    locale,
    propertyTicker,
    propertyName,
    propertyValuation,
    reservation,
    defaultCurrency,
    propertyContact,
    cart,
    isPropertyAvailable,
    propertyDiscountsList,
    origin,
    customPropertyName,
    comiNeto,
    propertyARI,
    cheapestBookableData,
    affiliate,
    tripAdvisorDataLayer,
    externalHotelId,
    propertyList,
    customOrigin,
    loyalty,
    brand,
    loyaltyCustomerId,
    mode
  ) => {
    let resNumHabs = 0;
    let totalAmount = 0;
    let totalAmountList = [];
    let amountRoom = [];
    let accommodations = [];
    let conditions = [];
    let mealPlans = [];
    let services = [];
    let promotions = [];
    const transactionProducts = [];
    let paymentMethod = "";
    let startDate = "";
    let endDate = "";
    let checkInDate = "";
    let checkOutDate = "";
    let customStartDate = "";
    let customEndDate = "";
    let totalNights = 0;
    let totalAdvance = 0;
    let promoCodes = "";
    const rangeStayDates = [];

    if (configuration) {
      startDate = formatDate(configuration.get("startDate"), "DD/MM/YYYY");
      endDate = formatDate(configuration.get("endDate"), "DD/MM/YYYY");
      checkInDate = formatDate(configuration.get("startDate"), "YYYY-MM-DD");
      checkOutDate = formatDate(configuration.get("endDate"), "YYYY-MM-DD");
      customStartDate = formatDate(configuration.get("startDate"), "DD-MM-YYYY");
      customEndDate = formatDate(configuration.get("endDate"), "DD-MM-YYYY");
      totalNights = dateDiffInDays(configuration.get("startDate"), configuration.get("endDate"));
      totalAdvance = dateDiffInDays(new Date(), configuration.get("startDate"));
      promoCodes = configuration.get("promoCodes");

      const startDateRange = moment(configuration.get("startDate"));
      const endDateRange = moment(configuration.get("endDate"));
      while (startDateRange.isSameOrBefore(endDateRange)) {
        rangeStayDates.push(startDateRange.format("YYYY-MM-DD"));
        startDateRange.add(1, "days");
      }
    }

    if (reservation && reservation.get("roomReservations")) {
      totalNights = 0;
      const checkIn = new Date(reservation.get("roomReservations").get(0).get("checkIn"));
      const checkOut = new Date(reservation.get("roomReservations").get(0).get("checkOut"));

      startDate = formatDate(checkIn, "DD/MM/YYYY");
      endDate = formatDate(checkOut, "DD/MM/YYYY");
      checkInDate = formatDate(checkIn, "YYYY-MM-DD");
      checkOutDate = formatDate(checkOut, "YYYY-MM-DD");
      customStartDate = formatDate(checkIn, "DD-MM-YYYY");
      customEndDate = formatDate(checkOut, "DD-MM-YYYY");
      totalAdvance = dateDiffInDays(new Date(), checkIn);
      paymentMethod = reservation.get("paymentMethod");

      const promoCodesList = [];

      if (reservation.get("promoCodes")) {
        reservation.get("promoCodes").map(promoCode => {
          promoCodesList.push(promoCode);
        });
      }
      promoCodes = promoCodesList.join(" | ");

      reservation.get("roomReservations").map(roomReservation => {
        for (let idx = 0; idx < roomReservation.get("quantity"); idx++) {
          const totalRate =
            (roomReservation.get("totalAmount") &&
              roomReservation.get("totalAmount").get("amount")) / roomReservation.get("quantity");

          totalAmountList.push(totalRate.toFixed(2));
          amountRoom.push((totalRate / roomReservation.get("nights")).toFixed(2));
        }

        resNumHabs += roomReservation.get("quantity");
        totalNights += roomReservation.get("nights");

        if (
          roomReservation.get("totalAmount") &&
          roomReservation.get("totalAmount").get("amount")
        ) {
          totalAmount += parseFloat(
            (
              roomReservation.get("totalAmount") && roomReservation.get("totalAmount").get("amount")
            ).toFixed(2)
          );
        }

        accommodations.push(roomReservation.get("accommodationType"));
        conditions.push(
          roomReservation.get("condition") && roomReservation.get("condition").get("name")
        );
        mealPlans.push(roomReservation.get("mealPlan"));

        let totalDiscount = 0;
        roomReservation.get("discounts").map(discount => {
          promotions.push(discount.get("name"));
          totalDiscount +=
            discount.get("totalDiscount") && discount.get("totalDiscount").get("amount");
        });

        transactionProducts.push({
          id: reservation.get("id"),
          name: roomReservation.get("accommodationTicker"),
          category:
            propertyARI &&
            propertyARI.get(propertyTicker) &&
            propertyARI.get(propertyTicker).get("inventories") &&
            propertyARI
              .get(propertyTicker)
              .get("inventories")
              .get(roomReservation.get("inventoryTicker")) &&
            propertyARI
              .get(propertyTicker)
              .get("inventories")
              .get(roomReservation.get("inventoryTicker"))
              .get("name"),
          amount: parseFloat(
            (roomReservation.get("subTotalAmount").get("amount") + totalDiscount).toFixed(2)
          ),
          quantity: roomReservation.get("nights"),
          rooms: roomReservation.get("quantity"),
          price: (
            roomReservation.get("totalAmount") &&
            roomReservation.get("totalAmount").get("amount") / roomReservation.get("nights")
          ).toFixed(2),
          sku: roomReservation.get("confirmationId"),
          depositStatus: calculateDepositStatus(roomReservation, reservation.get("paymentMethod")),
          paymentType: reservation.get("paymentTicker"),
        });

        roomReservation.get("selectedServices").map(service => {
          services.push(service.get("ticker"));
        });
      });
    } else if (cart && cart.reservations && cart.reservations.size > 0) {
      totalNights = 0;
      const cartStartDate = cart.reservations.get(0).get("startDate");
      const cartEndDate = cart.reservations.get(0).get("endDate");
      startDate = formatDate(cartStartDate, "DD/MM/YYYY");
      endDate = formatDate(cartEndDate, "DD/MM/YYYY");
      checkInDate = formatDate(cartStartDate, "YYYY-MM-DD");
      checkOutDate = formatDate(cartEndDate, "YYYY-MM-DD");
      customStartDate = formatDate(cartStartDate, "DD-MM-YYYY");
      customEndDate = formatDate(cartEndDate, "DD-MM-YYYY");
      totalAdvance = dateDiffInDays(
        new Date(),
        new Date(cart.reservations.get(0).get("startDate"))
      );

      cart.reservations.map(cartReservation => {
        let nights = 0;

        if (configuration) {
          nights = dateDiffInDays(configuration.get("startDate"), configuration.get("endDate"));
        }

        for (let idx = 0; idx < cartReservation.get("numberOfUnits"); idx++) {
          const totalRate = cartReservation.get("totalRate") / cartReservation.get("numberOfUnits");

          totalAmountList.push(totalRate.toFixed(2));
          amountRoom.push((totalRate / nights).toFixed(2));
        }

        resNumHabs += cartReservation.get("numberOfUnits");
        accommodations.push(cartReservation.get("inventory").get("accommodation").get("name"));
        totalNights += nights;
        totalAmount += parseFloat(cartReservation.get("totalRate").toFixed(2));
        conditions.push(cartReservation.get("inventory").get("condition").get("name"));
        mealPlans.push(cartReservation.get("inventory").get("mealplan").get("name"));

        cartReservation.get("selectedServices").map(service => {
          services.push(service.get("name"));
        });

        if (cartReservation.get("inventory").get("discountsApplied")) {
          cartReservation
            .get("inventory")
            .get("discountsApplied")
            .map(discount => {
              promotions.push(
                propertyDiscountsList.get(discount) &&
                  propertyDiscountsList.get(discount).get("name")
              );
            });
        }
      });
    }

    accommodations = accommodations.join(" | ");
    conditions = conditions.join(" | ");
    mealPlans = mealPlans.join(" | ");
    services = services.join(",");
    promotions = promotions.join(" | ");
    totalAmountList = totalAmountList.join(" | ");
    amountRoom = amountRoom.join(" | ");
    totalAmount = totalAmount && parseFloat(totalAmount);
    const activePropertyTickers =
      propertyList && propertyList.map(property => property.get("ticker"));

    const occupants = propertyARI.get(propertyTicker).get("occupants");
    // All strings must be in spanish regardless of the reservation's language
    const result = {
      content_engine_version: "v17",
      pageTitle: configuration && configuration.get("witbookerPageTitle"),
      pageLang: locale,
      propertyName,
      propertyTicker,
      customPropertyName,
      propertyValuation,
      propertyDestination: propertyContact && propertyContact.get("city"), // Marbella
      searchDestination: propertyContact && propertyContact.get("country"),
      searchAvailability: isPropertyAvailable,
      destinationName: propertyContact && propertyContact.get("city"), // marbella
      totalSeniors: occupants.get(constants.OCCUPANTS_TYPE.SENIOR), // Total of all bookings
      totalAdults: occupants.get(constants.OCCUPANTS_TYPE.ADULT), // Total of all bookings
      totalJuniors: occupants.get(constants.OCCUPANTS_TYPE.TEENAGER), // Total of all bookings
      totalChildren: occupants.get(constants.OCCUPANTS_TYPE.CHILDREN), // Total of all bookings
      totalBabies: occupants.get(constants.OCCUPANTS_TYPE.BABY), // Total of all bookings
      totalRooms: resNumHabs, // Total of all bookings
      roomName: accommodations, // Room names concatenated with " | " when multireservation
      startDate: startDate,
      endDate: endDate,
      checkInDate: checkInDate,
      checkOutDate: checkOutDate,
      transaction_date_iso: formatDate(new Date(), "YYYY-MM-DD"),
      totalNights: totalNights, // Total of all bookings
      conditionName: conditions, // Condition names concatenated with " | " when multireservation
      mealPlanName: mealPlans, // Meal plan names concatenated with " | " when multireservation
      promotionName: promotions, // Promotion names concatenated with " | "
      promoCodes,
      services: services, // Service names concatenated with " | "
      paymentError: "",
      totalAdvance: totalAdvance,
      origin,
      mode,
      price: (totalAmount / totalNights).toFixed(2),
      amountRoom, // Precio total || Precio por noche
      amount: totalAmountList, // Total prices concatenated with " | " when multireservation
      checkedAvailability: true, // True if user consulted disponibility
      searchCheapestInfo: cheapestBookableData,
      affiliate,
      rangeStayDates: rangeStayDates && rangeStayDates.join("|"),
      ...tripAdvisorDataLayer,
      externalHotelId,
      activePropertyTickers: activePropertyTickers && activePropertyTickers.join(","),
      isLoyaltyUser: Boolean(loyaltyCustomerId),
      customerId: loyaltyCustomerId,
      brandTicker: brand && brand.ticker,
      tierTicker: loyalty && loyalty.ticker,
      bookingDates: `${customStartDate}_${customEndDate}`,
      startDateAndNights: `${customStartDate}_${totalNights}`,
      affiliation: propertyTicker,
    };
    if (customOrigin) {
      result.customOrigin = customOrigin;
    }

    if (reservation) {
      result.transactionCardType = reservation.get("cardType");
      result.transactionId = reservation.get("id");
      result.transactionDate = formatDate(new Date(), "DD/MM/YYYY");
      result.transactionAffiliation = propertyTicker; // Standby
      result.transactionTotal = totalAmount;
      result.transactionTotalCents = totalAmount * 100;
      result.transactionShipping = "x";
      result.transactionTax =
        totalAmount && comiNeto && (totalAmount - totalAmount * comiNeto).toFixed(2);
      result.transactionPaymentType = paymentMethod; // Credit Card || Paypal ...
      result.transactionCurrency = defaultCurrency;
      result.transactionZip = ""; // Standby
      result.transactionShippingMethod = "x";
      result.transactionPromoCode = promoCodes;
      result.transactionProducts = transactionProducts;
    }

    return result;
  }
);

export const expandedPromotionsSelector = createSelector(
  getModelSelector,
  model => model && model.get("expandedPromotions")
);

export const promotionsListMode = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("promotionsListMode")
);

export const isBookingFormSummaryOpenedSelector = createSelector(
  getModelSelector,
  model => model && model.get("isBookingFormSummaryOpened")
);

export const cookieConsentSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("cookieConsent")
);

function isTabFiltersCollapsedUIFunction(width, propertyTabs) {
  const totalTabFilters =
    propertyTabs &&
    propertyTabs.reduce((result, filter) => {
      const options = (filter.get("options") && filter.get("options").size) || 0;
      result = result + options;
      return result;
    }, 0);
  return propertyTabs && (width < sizes.md || (width >= sizes.md && totalTabFilters > 4));
}

export const isTabFiltersCollapsedUISelector = createSelector(
  widthSelector,
  propertyTabsSelector,
  isTabFiltersCollapsedUIFunction
);

export const isExtraTabFiltersCollapsedUISelector = createSelector(
  widthSelector,
  extraFiltersSelector,
  isTabFiltersCollapsedUIFunction
);

export const isAgentSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("isAgent")
);

export const hideUnavailableURLScreenSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("hideUnavailableURLScreen")
);

export const showAgentLoginSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("showAgentLogin")
);

export const cancellationRestrictionsInventorySelector = createSelector(
  propertyARISelector,
  propertyTickerSelector,
  startDateSelector,
  endDateSelector,
  isPropertyChainSelector,
  (propertyARI, propertyTicker, startDate, endDate, isPropertyChain) =>
    !isPropertyChain && getCancellationRestrictions(propertyARI, propertyTicker, startDate, endDate)
);

export const cancellationPoliciesInfoInventorySelector = createSelector(
  propertyARISelector,
  propertyTickerSelector,
  startDateSelector,
  endDateSelector,
  isPropertyChainSelector,
  (propertyARI, propertyTicker, startDate, endDate, isPropertyChain) =>
    !isPropertyChain && getCancellationPoliciesInfo(propertyARI, propertyTicker, startDate, endDate)
);

export const cancellationRestrictionsCheckoutSelector = createSelector(
  propertyARISelector,
  propertyTickerSelector,
  cartSelector,
  (propertyARI, propertyTicker, cart) => {
    if (cart && cart.reservations && cart.reservations.size > 0) {
      return cart.reservations
        .toJS()
        .map(reservation =>
          getCancellationRestrictions(
            propertyARI,
            propertyTicker,
            reservation.startDate,
            reservation.endDate
          )
        );
    }
  }
);

export const cancellationPoliciesInfoCheckoutSelector = createSelector(
  propertyARISelector,
  propertyTickerSelector,
  cartSelector,
  (propertyARI, propertyTicker, cart) => {
    if (cart && cart.reservations && cart.reservations.size > 0) {
      return cart.reservations
        .toJS()
        .map(reservation =>
          getCancellationPoliciesInfo(
            propertyARI,
            propertyTicker,
            reservation.startDate,
            reservation.endDate
          )
        );
    }
  }
);

export const hideObligatoryExtrasSelector = createSelector(
  configurationSelector,
  configuration => configuration && !!configuration.get("hideObligatoryExtras")
);

export const showBestPriceGuaranteedSelector = createSelector(
  configurationSelector,
  configuration => configuration && !!configuration.get("showBestPriceGuaranteed")
);

export const assistedModeSelector = createSelector(
  propsSelector,
  props =>
    !!(
      (props && props.location && props.location.query && props.location.query.stay) ||
      getFromSessionStorage(constants.LOCAL_STORAGE_KEYS.STAY_QUEUE)
    )
);

export const showMultipleAccommodationsSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("showMultipleAccommodations")
);

export const showNewBookingFormSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("showNewBookingForm")
);

export const tagsSelector = createSelector(
  configurationSelector,
  restoredSessionSelector,
  (configuration, restoredSession) => {
    let tags = configuration && configuration.get("tags");
    if (restoredSession) {
      const { urlParams = [] } = restoredSession;
      const sessionTags = urlParams["tags"];
      if (sessionTags) {
        tags = sessionTags;
      }
    }
    return tags && tags.split(",");
  }
);

export const cityTaxConfigurationSelector = createSelector(
  channelSelector,
  affiliateSelector,
  getCityTaxConfiguration
);

export const taxesTextSelector = createSelector(
  configurationSelector,
  cityTaxConfigurationSelector,
  (configuration, cityTaxConfiguration) => {
    return cityTaxConfiguration === constants.CITY_TAX_CONFIGURATION.EXCLUDE_FROM_ALL_STEPS
      ? configuration && configuration.get("taxesText")
      : constants.TAXES_TEXT.TAXES_INCLUDED_FEES_EXCLUDED;
  }
);

export const checkoutTaxesTextSelector = createSelector(
  configurationSelector,
  cityTaxConfigurationSelector,
  (configuration, cityTaxConfiguration) => {
    return cityTaxConfiguration === constants.CITY_TAX_CONFIGURATION.EXCLUDE_FROM_ALL_STEPS
      ? configuration && configuration.get("taxesText")
      : constants.TAXES_TEXT.TAXES_AND_FEES_INCLUDED;
  }
);

export const ipInfoSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("ipInfo")
);

export const maxCartItemsSelector = createSelector(
  configurationSelector,
  configuration => (configuration && configuration.get("maxCartItems")) || 0
);

export const showCollapsedRoomSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("showCollapsedRoom")
);

export const depositRequiredTextConfigurationSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("depositRequiredTextConfiguration")
);

export const depositNotRequiredTextConfigurationSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("depositNotRequiredTextConfiguration")
);

export const compoundDiscountsSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("compoundDiscounts")
);
